import _isEqual from 'lodash/isEqual';
import _orderBy from 'lodash/orderBy';
import React from 'react';

export function useDrugsData({ data, referenceGene }) {
  let drugs = [];
  // We need to figure out which SpecificityIndicator to use based on
  // if it is in the "byGene" or "byGeneAndFeature" array of the "drugAssociations"
  let isAllByGene = false;
  let isAllByGeneAndFeature = false;

  if (data.drugAssociations && referenceGene) {
    const { byGene = [] } = data.drugAssociations;
    let { byGeneAndFeature = [] } = data.drugAssociations;

    // Check if all items are either "byGene" or "byGeneAndFeature" so
    // we can save ourselves a reduce potentially
    if (byGene.length > 0 && byGeneAndFeature.length === 0) {
      isAllByGene = true;
    } else if (byGene.length === 0 && byGeneAndFeature.length > 0) {
      isAllByGeneAndFeature = true;
    } else {
      // We have a mixture of "byGene" or "byGeneAndFeature", so we add a
      // "byGeneAndFeature" prop to each object in this array. Since they
      // are an exact gene match, they need a different SpecificityIndicator
      // and we will use this prop to determine what to display
      byGeneAndFeature = byGeneAndFeature.reduce((acc, g) => {
        const gene = {
          ...g,
          byGeneAndFeature: true,
        };
        acc.push(gene);
        return acc;
      }, []);
    }

    // Ensure only associations for the focused gene are shown
    drugs = [...byGene, ...byGeneAndFeature].filter(g =>
      g.gene.split(',').includes(referenceGene),
    );

    // We will apply default sorting, unless the user specifies their own sorting
    // via `handleSortClicked`.
    drugs = _orderBy(
      drugs,
      ['byGeneAndFeature', 'evidenceLevel'],
      ['asc', 'asc'],
    );
  }

  const [sortKeys, setSortKeys] = React.useState(null);
  const [sortDir, setSortDir] = React.useState(null);
  const [sortedData, setSortedData] = React.useState(drugs);
  const drugsRef = React.useRef([]);

  const handleSortClicked = ({ keys, directions }) => {
    setSortKeys(keys);
    setSortDir(directions);
  };

  // We leverage a ref for drugs so that we can ensure we only sort
  // when sort keys, sort directions, or the drugs entirely change.
  // Otherwise, we can run into cases where we get maximum call depth exceeded
  // errors if we are working with big data sets and users are clicking
  // fast
  if (!_isEqual(drugsRef.current, drugs)) {
    drugsRef.current = drugs;
  }

  React.useEffect(() => {
    if (sortKeys && sortDir) {
      setSortedData(_orderBy(drugsRef.current, sortKeys, sortDir));
      return;
    }

    // Sort was deselected completely, so default to un-sorted data
    setSortedData(drugsRef.current);
  }, [sortDir, sortKeys]);

  return {
    handleSortClicked,
    isAllByGene,
    isAllByGeneAndFeature,
    sortedData,
  };
}
